import { CustomButton, Button } from '@/components';
import { abilityConstants, dashboardConstants, userCan } from '@/lib';
import {
  computeRequestFilters,
  SalesOperationsExport,
  SalesOperationsFilters,
  SalesOperationsFilterTags,
  SalesOperationsRepsTable,
  SalesOperationsStats,
  SummerDropdownFilter,
  defaultFilters,
  normalizeRequestFilters,
  FILTER_NAMES,
  FILTER_VALUES,
} from '@/modules/dashboard/components';
import {
  repStatusesSelector,
  requestExperienceOptionsAsync,
  requestRepStatusesAsync,
  requestApartmentStatusesAsync,
} from '@/redux/reps';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { SearchBar } from '@/components/common';
import { requestSoRepsAsync } from '@/redux/sales-operations';
import { soRepsLoadingSelector } from '@/redux/loading';
import PropTypes from 'prop-types';
import { userRoleSelector, userSelector } from '@/redux/auth';
import { useRecruitSearchTypeOptions } from '@/hooks';
import { dataKeys } from '@/lib/adapters';
import {
  migrateHousingAsync,
  migrateRepsAsync,
} from '@/modules/AspynMigrator/redux';
import {
  migrateHousingLoadingSelector,
  migrateRepsLoadingSelector,
} from '@/redux/loading/selectors';
import { useFeatureFlag } from 'configcat-react';

const {
  ACCESS_ASPYN_MIGRATOR_ABILITY,
} = abilityConstants;

const {
  ASPYN_MIGRATOR_FEATURE_FLAG_NAME,
} = dashboardConstants;

const { RECRUIT } = dataKeys;
const initialPage = 1;
const initialPageSize = 10;
const initialSearchType = RECRUIT;

const SalesOperations = ({
  repsLoading,
  requestExperienceOptions,
  requestApartmentStatuses,
  requestRepStatuses,
  getReps,
  role,
  repStatuses,
  migrateHousing,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFiltersRaw] = useState(defaultFilters);
  const [requestFilters, setRequestFilters] = useState(computeRequestFilters(defaultFilters));
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedPage, setSelectedPage] = useState(initialPage);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(initialSearchType);
  const [repsToChangeStatus, setRepsToChangeStatus] = useState([]);

  const user = useSelector(userSelector);

  const { value: isAspynMigratorEnabled } = useFeatureFlag(
    ASPYN_MIGRATOR_FEATURE_FLAG_NAME,
    false,
  );

  const dispatch = useDispatch();
  const isMigrateHousingLoading = useSelector(migrateHousingLoadingSelector);
  const isMigrateRepsLoading = useSelector(migrateRepsLoadingSelector);

  const searchTypeOptions = useRecruitSearchTypeOptions(role);

  const getRepsWithFilters = () => {
    const params = {
      page: {
        number: selectedPage,
        size: pageSize,
      },
      filter: {},
    };

    const normalizedFilters = normalizeRequestFilters(requestFilters);
    normalizedFilters.forEach(({ name, value }) => {
      params.filter[name] = value;
    });

    if (searchText) {
      params.filter.search = searchText;
      params.filter.search_type = searchType;
    }

    getReps(params);
    setRepsToChangeStatus([]);
  };

  useEffect(() => {
    getRepsWithFilters();
  }, [requestFilters, pageSize, searchText, searchType, selectedPage]);

  useEffect(() => {
    requestExperienceOptions();
    requestRepStatuses();
    requestApartmentStatuses();
  }, [requestExperienceOptions, requestRepStatuses, requestApartmentStatuses]);

  const onSearchClick = useCallback(({ searchText, searchType }) => {
    setSearchText(searchText);
    setSearchType(searchType);
    setSelectedPage(initialPage);
    setRepsToChangeStatus([]);
  }, []);

  const onPageChange = useCallback(({ selected }) => {
    setSelectedPage(selected);
    setRepsToChangeStatus([]);
  }, []);

  const setFilters = useCallback((nextFilters) => {
    const nextRequestFilters = computeRequestFilters(nextFilters);

    if (!isEqual(filters, nextFilters)) {
      setFiltersRaw(nextFilters);
    }

    if (!isEqual(requestFilters, nextRequestFilters)) {
      setRequestFilters(nextRequestFilters);
    }
  }, [filters, requestFilters]);

  const getFilterTagText = useCallback((name, value) => name === FILTER_NAMES.STATUS
    ? repStatuses.find((repStatus) => repStatus.statusCode === value)?.statusTitle
    : value, [repStatuses]);

  const toggleFiltersModal = useCallback(() => {
    setFiltersOpen(!filtersOpen);
  }, [filtersOpen]);

  const handlePushToAspyn = useCallback(() => {
    if (filters?.recruitingSeason !== FILTER_VALUES.DEFAULT) {
      migrateHousing({ recruitingSeasonId: filters.recruitingSeason });
    } else {
      migrateHousing();
    }
  }, [filters?.recruitingSeason, migrateHousing]);

  const handleMigrateReps = useCallback(() => {
    const userIds = repsToChangeStatus.map((rep) => rep.id);

    dispatch(migrateRepsAsync.request({ userIds }));
    setRepsToChangeStatus([]);
  }, [dispatch, repsToChangeStatus]);

  return (
    <>
      <div className="px-8 py-3.5 bg-white border-b">
        <div className="flex items-center">
          <div className="shrink-0 text-gray-900 font-semibold">
            {dashboardConstants.SALES_OPERATIONS}
          </div>
          <SalesOperationsFilterTags
            filters={filters}
            setFilters={setFilters}
            getText={getFilterTagText}
          />
          <div className="flex ml-auto space-x-2">
            {isAspynMigratorEnabled && (
              <CustomButton
                color="white"
                disabled={isMigrateHousingLoading}
                onClick={handlePushToAspyn}
                className={`px-6 ${isMigrateHousingLoading ? 'text-gray-500' : 'text-gray-900'} font-normal`}
              >
                {isMigrateHousingLoading ? 'Pushing...' : 'Push Housing to Aspyn (Based on selected recruiting season)'}
              </CustomButton>
            )}
            <SummerDropdownFilter
              filters={filters}
              setFilters={setFilters}
            />
            <CustomButton
              color="white"
              onClick={toggleFiltersModal}
              className="px-6 text-gray-500 font-normal"
            >
              {dashboardConstants.FILTERS}
            </CustomButton>
            <SalesOperationsExport
              requestFilters={requestFilters}
              search={searchText}
              searchType={searchType}
            />
          </div>
        </div>
      </div>
      <div className="px-8">
        <SalesOperationsStats
          requestFilters={requestFilters}
          filters={filters}
          setFilters={setFilters}
          searchText={searchText}
          searchType={searchType}
        />
        <div className="mt-6 mb-7 flex justify-end items-center">
          {isAspynMigratorEnabled && userCan(user, ACCESS_ASPYN_MIGRATOR_ABILITY) && (
            <Button
              onClick={handleMigrateReps}
              disabled={!repsToChangeStatus.length || isMigrateRepsLoading}
              color="blue"
              className="mr-auto"
            >
              {isMigrateRepsLoading ? 'Migrating...' : `Migrate ${repsToChangeStatus.length > 1 ? `${repsToChangeStatus.length} reps` : 'rep'} to Aspyn`}
            </Button>
          )}
          <SearchBar
            searchType={searchType}
            searchText={searchText}
            typeOptions={searchTypeOptions}
            inputName={dashboardConstants.REP_SEARCH_NAME}
            selectName={dashboardConstants.REP_TYPE_FILTER_NAME}
            disabled={Boolean(repsLoading)}
            onSearchClick={onSearchClick}
            isSearchTypeSupported={!!searchTypeOptions.length}
          />
        </div>
        <SalesOperationsRepsTable
          getReps={getRepsWithFilters}
          pageSize={pageSize}
          setPageSize={setPageSize}
          onPageChange={onPageChange}
          selectedPage={selectedPage}
          initialPage={initialPage}
          recruitingSeasonId={filters?.recruitingSeason}
          repsToChangeStatus={repsToChangeStatus}
          setRepsToChangeStatus={setRepsToChangeStatus}
        />
      </div>
      <SalesOperationsFilters
        modalOpen={filtersOpen}
        closeModal={toggleFiltersModal}
        filters={filters}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
    </>
  );
};

SalesOperations.propTypes = {
  repsLoading: PropTypes.bool,
  getReps: PropTypes.func,
  requestExperienceOptions: PropTypes.func,
  requestApartmentStatuses: PropTypes.func,
  requestRepStatuses: PropTypes.func,
  role: PropTypes.string,
  repStatuses: PropTypes.arrayOf(PropTypes.object),
  migrateHousing: PropTypes.func,
};

const mapStateToProps = (state) => ({
  repsLoading: soRepsLoadingSelector(state),
  role: userRoleSelector(state),
  repStatuses: repStatusesSelector(state),
});

const mapDispatchToProps = {
  getReps: requestSoRepsAsync.request,
  requestExperienceOptions: requestExperienceOptionsAsync.request,
  requestApartmentStatuses: requestApartmentStatusesAsync.request,
  requestRepStatuses: requestRepStatusesAsync.request,
  migrateHousing: migrateHousingAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesOperations);
